/**
 * Edits ONE section of the personal profile.
 *
 * WHY PER-SECTION AND NOT ONE BIG EDIT MODE
 * -----------------------------------------
 * `edit-profile.tsx` had a single "Edit" toggle that flipped the whole screen
 * into edit mode with one Save at the bottom. Three things followed from that:
 * validation could only run at submit time (so errors arrived as a blocking
 * dialog naming a field you could no longer see), changing one value rewrote
 * every column, and each field needed two separate JSX trees — a read one and
 * an edit one — which is how they drifted apart.
 *
 * A section is the unit a user actually thinks in ("my details", "my bio"), and
 * it is small enough that its fields fit above the keyboard. The investor
 * profile already worked this way; this brings the shared personal profile onto
 * the same model.
 *
 * The write is a PATCH carrying only this section's fields — see
 * `src/lib/profile/profile-edit-fields.ts` for why that matters.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, type TextInput } from 'react-native';

import { AppModal, ModalAction } from '@/components/modal';
import {
  ProfileTextField,
  ProfileChoiceField,
  type ProfileFieldKind,
} from './ProfileSectionFields';
import {
  PERSONAL_EDITOR_FIELDS,
  type PersonalEditSection,
} from '@/lib/profile/profile-edit-fields';

/** Job titles a founder can hold. NOT `profiles.role` — see the removed-fields note. */
export const FUNCTION_TITLE_OPTIONS = [
  { value: 'founder', label: 'Founder' },
  { value: 'co-founder', label: 'Co-founder' },
  { value: 'ceo', label: 'CEO' },
  { value: 'cto', label: 'CTO' },
  { value: 'cfo', label: 'CFO' },
  { value: 'coo', label: 'COO' },
  { value: 'team-member', label: 'Team Member' },
] as const;

interface FieldSpec {
  key: string;
  label: string;
  placeholder: string;
  kind: ProfileFieldKind;
  required?: boolean;
  hint?: string;
  /** Rendered as a pill picker rather than a text input. */
  choices?: readonly { value: string; label: string }[];
}

const FIELD_SPECS: Record<string, FieldSpec> = {
  name: {
    key: 'name',
    label: 'Full name',
    placeholder: 'Your name',
    kind: 'name',
    required: true,
  },
  functionTitle: {
    key: 'functionTitle',
    label: 'Your role at the company',
    placeholder: 'Founder',
    kind: 'jobTitle',
    choices: FUNCTION_TITLE_OPTIONS,
  },
  location: {
    key: 'location',
    label: 'Location',
    placeholder: 'City, Country',
    kind: 'location',
  },
  linkedIn: {
    key: 'linkedIn',
    label: 'LinkedIn',
    placeholder: 'linkedin.com/in/yourprofile',
    kind: 'url',
    hint: 'Your public profile URL.',
  },
  bio: {
    key: 'bio',
    label: 'About you',
    placeholder: 'A brief introduction — what you work on and what you are looking for.',
    kind: 'longText',
  },
};

export const SECTION_TITLES: Record<PersonalEditSection, string> = {
  generalInfo: 'General info',
  about: 'About',
};

interface ProfileSectionModalProps {
  visible: boolean;
  section: PersonalEditSection | null;
  initialData: Record<string, string>;
  /** Startup members only — the title field has nowhere to go otherwise. */
  showFunctionTitle: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (data: Record<string, string>) => void;
}

export function ProfileSectionModal({
  visible,
  section,
  initialData,
  showFunctionTitle,
  isSaving,
  onClose,
  onSave,
}: ProfileSectionModalProps) {
  const [form, setForm] = useState<Record<string, string>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reseed when the editor OPENS on a section, so a cancelled edit does not leak
  // into the next one.
  //
  // `initialData` is deliberately NOT a dependency. The parent rebuilds that
  // object on every render, so depending on it would reseed the form — wiping
  // whatever the user had typed — any time the parent re-rendered for an
  // unrelated reason, such as a React Query refetch landing while the modal is
  // open. Opening is the only moment the seed is meant to be read.
  useEffect(() => {
    if (!visible || !section) return;
    setForm(initialData);
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, section]);

  const fields = useMemo<FieldSpec[]>(() => {
    if (!section) return [];
    return PERSONAL_EDITOR_FIELDS[section]
      .map((key) => FIELD_SPECS[key])
      .filter((spec): spec is FieldSpec => {
        if (!spec) return false;
        if (spec.key === 'functionTitle' && !showFunctionTitle) return false;
        return true;
      });
  }, [section, showFunctionTitle]);

  // One ref per TEXT field, in render order, so Next can hand focus along.
  // Choice fields are skipped — there is nothing to focus.
  const textFields = fields.filter((f) => !f.choices);
  const refs = useRef<(TextInput | null)[]>([]);
  refs.current = textFields.map((_, i) => refs.current[i] ?? null);

  const update = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear the error as soon as the user starts fixing it, rather than making
    // them submit again to find out.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  };

  const handleSave = () => {
    const nextErrors: Record<string, string> = {};
    for (const spec of fields) {
      if (spec.required && !(form[spec.key] ?? '').trim()) {
        nextErrors[spec.key] = `${spec.label} is required.`;
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      // Inline, next to the field — not a modal alert naming a field the user
      // can no longer see.
      setErrors(nextErrors);
      return;
    }
    onSave(form);
  };

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title={section ? SECTION_TITLES[section] : ''}
      footer={
        <AppModal.Actions>
          <ModalAction label="Cancel" variant="neutral" onPress={onClose} disabled={isSaving} />
          <ModalAction
            label={isSaving ? 'Saving…' : 'Save'}
            variant="primary"
            onPress={handleSave}
            disabled={isSaving}
          />
        </AppModal.Actions>
      }
    >
      <View>
        {fields.map((spec) => {
          if (spec.choices) {
            return (
              <ProfileChoiceField
                key={spec.key}
                label={spec.label}
                value={form[spec.key] ?? ''}
                options={spec.choices}
                onChange={(v) => update(spec.key, v)}
              />
            );
          }

          const index = textFields.findIndex((f) => f.key === spec.key);
          const isLast = index === textFields.length - 1;

          return (
            <ProfileTextField
              key={spec.key}
              ref={(node) => {
                refs.current[index] = node;
              }}
              label={spec.label}
              value={form[spec.key] ?? ''}
              onChangeText={(v) => update(spec.key, v)}
              placeholder={spec.placeholder}
              kind={spec.kind}
              required={spec.required}
              hint={spec.hint}
              error={errors[spec.key] || undefined}
              // Chaining the focus is what keeps the keyboard up between fields.
              // The last field gets no handler, so it shows Done and closes it.
              onSubmitEditing={
                isLast ? undefined : () => refs.current[index + 1]?.focus()
              }
            />
          );
        })}
      </View>
    </AppModal>
  );
}
